import axios from 'axios'

import { Comment } from 'common/server/comment'
import { OrderDelivery } from 'common/server/deliveries'
import { OrderHistoryResponse } from 'common/server/order_history'
import { OrderMaterial } from 'common/server/orders'
import {
  File,
  OrderStates,
  OrderSubStates,
  RelationshipStockStatus,
  SearchKeysResponse,
  StandardIndexRequest,
  StandardIndexResponse,
} from 'common/server/server_types'

import { Delivery, DeliveryUpdateParams } from 'vendor/server/deliveries'

export interface Order {
  updated_at: string
  comments: Comment[]
  company_material_columns: string[]
  confirmed_at: string
  total_invoiced_amount: number
  quote_scheduled_to_expire_at?: string
  deliveries: Delivery[]
  id: string
  order_blocking_enabled: boolean
  order_materials: OrderMaterial[]
  order_number: string
  order_package_name: string
  project: { name: string; project_id: string; tax_line_items: { enabled: boolean } }
  public_purchaser_files: File[]
  state: OrderStates
  sub_state: OrderSubStates
  company_user: {
    id: string
    full_name: string
    company_name: string
  }
  company_vendor: {
    id: string
    vendor_name: string
    vendor?: {
      id: string
      name: string
      email: string
    }
  }
  vendor_contacts: {
    id: string
    first_name: string
    last_name: string
    email: string
    vendor_user?: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }[]
  vendor_files: File[]
  purchase_order?: File
  quote?: File[]
  cancellation_rejected_at?: string
  cancellation_accepted_at?: string
  cancellation_requested_at?: string
  cancellation_requested_reason?: string
  vendor_price_lock_enabled: boolean
}

export interface OrderUpdateParams {
  id: string
  updated_at: string
  state_changes?: {
    confirmed_at?: string
    rejected_at?: string
    cancellation_rejected_at?: string
    cancellation_accepted_at?: string
  }
  order_materials?: OrderMaterial[]
  vendor_files_signed_ids?: string[]
  vendor_files_delete_ids?: string[]
  quote_signed_id?: string
  comment?: string
  deliveries?: DeliveryUpdateParams[]
  quote_scheduled_to_expire_at?: string
  vendor_user_ids?: string[] // It is used by vendors to change company vendor contacts
}

export interface IndexOrderRecord {
  id: string
  name: string
  company_name: string
  watcher_user_names: Array<string>
  delivery_issue_types: string
  delivery_issues: string
  draft_vendor_names: Array<string>
  invoice_ids: Array<string>
  invoice_numbers: string[]
  invoiced: string
  manual_export_status: string
  order_created_at: string
  order_number: string
  order_updated_at: string
  ordered_at: string
  ordered_by_name: string
  package_name: string
  project_group_name: string
  project_name: string
  project_number: string
  project_name_with_company: string
  quote_number: string
  requested_by_name: string
  state: OrderStates
  sub_state: OrderSubStates
  sync_status: string
  tags: string[]
  user_email: string
  user_first_name: string
  user_last_name: string
  vendor_name: string
  vendor_user: string
  created_at: string
  updated_at: string
  company_id: string
  company_vendor_id: string
  external_vendor_id: string
  order_id: string
  ordered_by_id: string
  project_group_id: string
  project_id: string
  requested_by_id: string
  user_id: string
  deliveries: Array<OrderDelivery>
  is_draft: boolean
  agave_sync_status: RelationshipStockStatus
  has_open_delivery_issue: boolean
  lead_time_cutoff_date_timestamp: number
  order_package_id: string
  last_action: {
    author_type: 'User' | 'VendorUser' | 'CompanyVendorContact' | 'SubBase'
    author_name: string
    created_at: string
  }
  grand_total: number
  quote_scheduled_to_expire_at: string
  invoices: Array<{
    id: string
    number: string
  }>
}

export interface FacetsOrder {
  [key: string]: string | Array<string>
}

export type StateCounts = {
  [stateName: string]: number
}

export type IndexResponse = StandardIndexResponse<IndexOrderRecord> & {
  state_counts: StateCounts
}

export function search_key() {
  return axios.get<SearchKeysResponse>('/vendor/orders/search_key')
}

export function show(id: string) {
  return axios.get<Order>(`/vendor/order/${id}`)
}
export function update(payload: OrderUpdateParams) {
  return axios.patch(`/vendor/order/${payload['id']}`, payload)
}

export function history(id: string) {
  return axios.get<{ history: OrderHistoryResponse[] }>(`/vendor/order/${id}/history`)
}

export function comment(id: string, comment: string) {
  return axios.post(`/vendor/order/${id}/comment`, { comment })
}

export function index(request: StandardIndexRequest) {
  const { filters, ...restRequest } = request
  const url = `/vendor/consolidated_orders`

  return axios.get<IndexResponse>(url, {
    params: {
      ...filters,
      ...restRequest,
    },
  })
}

export function facets() {
  const url = `/vendor/consolidated_orders/facets`

  return axios.get<FacetsOrder>(url)
}
